import { useEffect, useState } from "react";
import axios from "axios";
import { ModalShell, inputStyle } from "@/pages/AdminModals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = window.location.origin;
const F = "'IBM Plex Sans', sans-serif";
const btn = { padding: "4px 10px", background: "linear-gradient(180deg,#D8E0F0,#B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, fontFamily: F, fontSize: 12, fontWeight: 600, color: "#223", cursor: "pointer" };
const STATUS = { live: ["#007A33", "LIVE"], stale: ["#B36B00", "STALE"], lost: ["#B00", "OFFLINE"], never: ["#666", "NEVER SEEN"] };

function copy(text) { navigator.clipboard?.writeText(text).catch(() => {}); }

function DeviceRow({ d, units, onChange }) {
  const [st, label] = STATUS[d.status] || STATUS.never;
  const ingest = d.token ? `${BASE}/api/gps/ingest/${d.token}` : "";
  const patch = (body) => axios.patch(`${API}/gps/devices/${d.id}`, body).then(onChange);
  return (
    <div data-testid={`gps-device-${d.id}`} style={{ background: "#E8EEF6", border: "1px solid #99AABB", borderRadius: 3, padding: 8, marginBottom: 6, fontFamily: F, fontSize: 12, color: "#223" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <b style={{ fontSize: 13 }}>{d.name}</b>
        <span style={{ fontSize: 10, padding: "0 4px", background: "#334", color: "#FFF", borderRadius: 2 }}>{d.kind.toUpperCase()}</span>
        <span data-testid={`gps-device-status-${d.id}`} style={{ color: st, fontWeight: 700 }}>● {label}</span>
        {d.last_seen && <span style={{ color: "#556" }}>last {new Date(d.last_seen).toLocaleTimeString("en-US", { hour12: false })}</span>}
        <span style={{ flex: 1 }} />
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" data-testid={`gps-device-enabled-${d.id}`} checked={d.enabled !== false} onChange={(e) => patch({ enabled: e.target.checked })} /> Enabled
        </label>
        <button style={{ ...btn, color: "#900" }} data-testid={`gps-device-delete-${d.id}`}
          onClick={() => window.confirm(`Delete ${d.name}?`) && axios.delete(`${API}/gps/devices/${d.id}`).then(onChange)}>Delete</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <span style={{ minWidth: 70 }}>Unit:</span>
        <select data-testid={`gps-device-unit-${d.id}`} value={d.unit_id || ""} disabled={d.kind === "mdt"} onChange={(e) => patch({ unit_id: e.target.value || null })} style={{ ...inputStyle, flex: "0 0 220px" }}>
          <option value="">-- Unassigned --</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      {d.kind === "cradlepoint" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <span style={{ minWidth: 70 }}>Router ID:</span>
          <input data-testid={`gps-device-routerid-${d.id}`} defaultValue={d.router_id || ""} maxLength={16} placeholder="e.g. BSVAC2"
            onBlur={(e) => { const v = e.target.value.trim().toUpperCase(); if (v !== (d.router_id || "")) patch({ router_id: v }).catch((er) => { alert(er?.response?.data?.detail || "Could not save Router ID"); e.target.value = d.router_id || ""; }); }}
            style={{ ...inputStyle, flex: "0 0 140px", textTransform: "uppercase", fontWeight: 700 }} />
          <span style={{ color: "#556", fontSize: 11 }}>= router's "Prepend Unique Identifier" (Custom)</span>
        </div>
      )}
      {ingest && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ minWidth: 70 }}>{d.kind === "tracker" ? "Tracker link:" : "Alt. HTTPS:"}</span>
          <code data-testid={`gps-device-url-${d.id}`} style={{ background: "#FFF", border: "1px solid #AAB", padding: "2px 4px", fontSize: 11, wordBreak: "break-all", flex: 1 }}>
            {d.kind === "tracker" ? `${BASE}/tracker?t=${d.token}` : ingest}
          </code>
          <button style={btn} data-testid={`gps-device-copy-${d.id}`} onClick={() => copy(d.kind === "tracker" ? `${BASE}/tracker?t=${d.token}` : ingest)}>Copy</button>
          <button style={btn} data-testid={`gps-device-rotate-${d.id}`} onClick={() => window.confirm("Generate a new token? The old URL stops working immediately.") && axios.post(`${API}/gps/devices/${d.id}/rotate-token`).then(onChange)}>New Token</button>
          {d.kind === "cradlepoint" && (
            <a style={{ ...btn, textDecoration: "none" }} data-testid={`gps-device-download-${d.id}`} href={`${API}/gps/devices/${d.id}/cradlepoint-app?base=${encodeURIComponent(BASE)}`}>Router App (.zip)</a>
          )}
        </div>
      )}
    </div>
  );
}

function GatewayPanel({ settings, setSettings }) {
  const seen = settings.gateway_last_seen ? (Date.now() - new Date(settings.gateway_last_seen).getTime()) / 1000 : null;
  const online = seen != null && seen < 30;
  return (
    <div data-testid="gps-gateway-panel" style={{ background: "#E8EEF6", border: "1px solid #6688CC", borderRadius: 3, padding: 8, marginBottom: 10, lineHeight: 1.55 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <b style={{ fontSize: 13 }}>CAD GPS Gateway</b>
        <span data-testid="gps-gateway-status" style={{ color: online ? "#007A33" : "#B00", fontWeight: 700 }}>
          ● {online ? "ONLINE" : settings.gateway_last_seen ? "OFFLINE" : "NOT INSTALLED"}
        </span>
        {settings.gateway_last_seen && <span style={{ color: "#556" }}>last {new Date(settings.gateway_last_seen).toLocaleTimeString("en-US", { hour12: false })} from {settings.gateway_ip}</span>}
        <span style={{ flex: 1 }} />
        <a style={{ ...btn, textDecoration: "none" }} data-testid="gps-gateway-download" href={`${API}/gps/gateway/bundle?base=${encodeURIComponent(BASE)}`}>Download Gateway (.zip)</a>
        <button style={btn} data-testid="gps-gateway-newkey" onClick={() => window.confirm("Generate a new gateway key? The installed gateway stops forwarding until you update its key.") && axios.post(`${API}/gps/gateway/key`).then((r) => setSettings(r.data))}>New Key</button>
      </div>
      <div style={{ fontSize: 11, color: "#334" }}>
        Router setup (NetCloud → Configuration → System → GPS → Send to Server): <b>Server</b> = gateway public IP · <b>Port</b> = 5010 · <b>Use UDP</b> = off ·
        <b> Language</b> = NMEA (RMC + GGA) · <b>Prepend Unique Identifier</b> = on, Custom = the device's Router ID · <b>Interval</b> = 2s.
      </div>
    </div>
  );
}

export function GPSDevicesModal({ onClose }) {
  const [devices, setDevices] = useState([]);
  const [units, setUnits] = useState([]);
  const [settings, setSettings] = useState({ traccar_fallback: false });
  const [form, setForm] = useState({ name: "", kind: "cradlepoint", unit_id: "", router_id: "" });
  const [unknown, setUnknown] = useState([]);

  const load = () => {
    axios.get(`${API}/gps/devices`).then((r) => setDevices(r.data)).catch(() => {});
    axios.get(`${API}/gps/unknown-routers`).then((r) => setUnknown(r.data)).catch(() => {});
    axios.get(`${API}/gps/settings`).then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    axios.get(`${API}/units`).then((r) => setUnits([...r.data].sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await axios.post(`${API}/gps/devices`, { ...form, unit_id: form.unit_id || null, router_id: form.kind === "cradlepoint" ? form.router_id : null });
      setForm({ name: "", kind: form.kind, unit_id: "", router_id: "" });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Could not add device"); }
  };

  return (
    <ModalShell title="GPS Tracking — Devices" onClose={onClose} width={820}>
      <div data-testid="gps-devices-modal" style={{ fontFamily: F, fontSize: 12, color: "#223" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
          <input data-testid="gps-new-name" placeholder="Device name (e.g. BSVAC-2 ROUTER)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <select data-testid="gps-new-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} style={{ ...inputStyle, flex: "0 0 170px" }}>
            <option value="cradlepoint">Cradlepoint router</option>
            <option value="tracker">Tablet / laptop tracker</option>
          </select>
          <select data-testid="gps-new-unit" value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} style={{ ...inputStyle, flex: "0 0 170px" }}>
            <option value="">-- Unit --</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {form.kind === "cradlepoint" && (
            <input data-testid="gps-new-routerid" placeholder="Router ID" maxLength={16} value={form.router_id}
              onChange={(e) => setForm({ ...form, router_id: e.target.value.toUpperCase() })} style={{ ...inputStyle, flex: "0 0 100px" }} />
          )}
          <button data-testid="gps-add-device-btn" style={btn} onClick={add}>Add Device</button>
        </div>
        <GatewayPanel settings={settings} setSettings={setSettings} />
        {unknown.length > 0 && (
          <div data-testid="gps-unknown-routers" style={{ background: "#FFF4D6", border: "1px solid #D9A400", borderRadius: 3, padding: 8, marginBottom: 10 }}>
            <b>Unrecognized routers</b> — the gateway is receiving GPS from these Router IDs but they aren't set up yet:
            {unknown.map((u) => (
              <div key={u.router_id} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <code style={{ fontWeight: 700 }}>{u.router_id || "(blank)"}</code>
                <span style={{ color: "#665" }}>last {new Date(u.last_seen).toLocaleTimeString("en-US", { hour12: false })}</span>
                <button style={btn} data-testid={`gps-unknown-add-${u.router_id}`}
                  onClick={() => setForm({ name: `${u.router_id} ROUTER`, kind: "cradlepoint", unit_id: "", router_id: u.router_id })}>Set up new</button>
                <select data-testid={`gps-unknown-link-${u.router_id}`} value="" style={{ ...inputStyle, flex: "0 0 200px" }}
                  onChange={(e) => e.target.value && axios.patch(`${API}/gps/devices/${e.target.value}`, { router_id: u.router_id }).then(load).catch((er) => alert(er?.response?.data?.detail || "Could not link"))}>
                  <option value="">Link to existing device…</option>
                  {devices.filter((d) => d.kind === "cradlepoint").map((d) => <option key={d.id} value={d.id}>{d.name}{d.unit_name ? ` (${d.unit_name})` : ""}</option>)}
                </select>
                <button style={{ ...btn, color: "#900" }} onClick={() => axios.delete(`${API}/gps/unknown-routers/${encodeURIComponent(u.router_id)}`).then(load)}>Dismiss</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 11, color: "#445", marginBottom: 10, lineHeight: 1.5 }}>
          Tablets/laptops: open the tracker link. EMS MDTs share automatically after crew login. Priority when several devices report for one unit: Router → Tracker → MDT.
          Stop rule: under 3 MPH within ~100 ft for 45s; resumes after moving ~250 ft or over 8 MPH on 2 fixes in a row.
        </div>
        {devices.length === 0 && <div style={{ color: "#556", padding: 8 }}>No GPS devices yet.</div>}
        {devices.map((d) => <DeviceRow key={d.id} d={d} units={units} onChange={load} />)}
        <div style={{ borderTop: "1px solid #8899AA", margin: "10px 0" }} />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
          <input type="checkbox" data-testid="gps-traccar-fallback" checked={!!settings.traccar_fallback}
            onChange={(e) => axios.put(`${API}/gps/settings`, { traccar_fallback: e.target.checked }).then((r) => setSettings(r.data))} />
          Legacy Traccar fallback — use Traccar positions only for units with no live native GPS
        </label>
      </div>
    </ModalShell>
  );
}
